import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { auth } from "../firebaseConfig";
import { addClothing, fetchClothes } from "../components/clothingService";
import { analyzeClothingImage } from "../services/clothingAnalysisService";
import { PLAN_LIMITS } from "../services/userService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";

// Image helpers (lightweight copy from modal)
const MAX_IMAGE_BYTES = 900_000;
const MAX_DIMENSION = 1280;
const DEFAULT_QUALITY = 0.85;
const MIN_QUALITY = 0.4;

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

async function compressImage(file) {
  const originalDataUrl = await readFileAsDataURL(file);
  const image = await loadImage(originalDataUrl);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  let { width, height } = image;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, 0, 0, width, height);

  let quality = DEFAULT_QUALITY;
  let output = canvas.toDataURL("image/jpeg", quality);

  while (output.length > MAX_IMAGE_BYTES && quality >= MIN_QUALITY) {
    quality -= 0.1;
    output = canvas.toDataURL("image/jpeg", quality);
  }

  if (output.length > MAX_IMAGE_BYTES) {
    throw new Error("Image trop volumineuse même après compression.");
  }

  return output;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const ImportContext = createContext({
  isActive: false,
  total: 0,
  processed: 0,
  failed: 0,
  hidden: false,
  lastAdded: null,
  startBulkImport: async (_files) => {},
  hideBanner: () => {},
});

export const useImport = () => useContext(ImportContext);

export const ImportProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [total, setTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [failed, setFailed] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const runningRef = useRef(false);

  const analyzeWithRetry = useCallback(async (dataUrl, attempts = 8) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        return await analyzeClothingImage(dataUrl);
      } catch (e) {
        lastErr = e;
        const msg = String(e?.message || '');
        const is429 = e?.status === 429 || msg.includes('rate_limit');
        if (is429) {
          if (typeof e.retryAfterMs === 'number' && e.retryAfterMs > 0) {
            await sleep(e.retryAfterMs + 200);
            continue;
          }
          let wait = 1000 * Math.pow(2, i) + Math.floor(Math.random() * 400);
          const m = msg.match(/try again in\s+(\d+)ms/i);
          if (m && m[1]) wait = parseInt(m[1], 10) + 250;
          await sleep(wait);
          continue;
        }
        break;
      }
    }
    throw lastErr;
  }, []);

  const computeMaxClothes = useCallback((userProfile) => {
    const plan = userProfile?.plan || "free";
    let maxClothes;
    if (ALWAYS_PREMIUM) {
      maxClothes = null;
    } else {
      const userLimit = userProfile?.limits?.maxClothes;
      const planLimit = PLAN_LIMITS[plan]?.maxClothes;
      if (userLimit !== undefined) maxClothes = userLimit;
      else if (planLimit !== undefined) maxClothes = planLimit;
      else maxClothes = PLAN_LIMITS.free.maxClothes;
    }
    return maxClothes;
  }, []);

  const startBulkImport = useCallback(async (files, userProfile) => {
    if (!auth.currentUser || !files || files.length === 0) return;
    if (runningRef.current) return; // simple guard: one batch at a time

    // Cap batch size for quotas
    const MAX_FILES_PER_BATCH = 20;
    let allowedFiles = Array.from(files);
    if (allowedFiles.length > MAX_FILES_PER_BATCH) {
      allowedFiles = allowedFiles.slice(0, MAX_FILES_PER_BATCH);
    }

    // Plan limit
    const maxClothes = computeMaxClothes(userProfile);
    if (maxClothes !== null) {
      try {
        const existing = await fetchClothes(auth.currentUser.uid);
        const remaining = Math.max(0, maxClothes - existing.length);
        if (remaining <= 0) {
          // nothing to import
          return;
        }
        if (allowedFiles.length > remaining) {
          allowedFiles = allowedFiles.slice(0, remaining);
        }
      } catch (e) {}
    }

    if (allowedFiles.length === 0) return;

    // Init UI
    setHidden(false);
    setIsActive(true);
    setTotal(allowedFiles.length);
    setProcessed(0);
    setFailed(0);
    runningRef.current = true;

    try {
      for (const file of allowedFiles) {
        try {
          // Prepare image
          let dataUrl;
          try {
            dataUrl = await compressImage(file);
          } catch (cmpErr) {
            dataUrl = await readFileAsDataURL(file);
            if (!dataUrl || dataUrl.length > MAX_IMAGE_BYTES) {
              throw new Error("Image trop volumineuse");
            }
          }

          // Analyze via IA (strict)
          const suggestion = await analyzeWithRetry(dataUrl, 8);
          if (!suggestion || !suggestion.type) {
            throw new Error("Analyse IA incomplète (type manquant)");
          }

          const payload = {
            type: suggestion.type,
            colors: Array.isArray(suggestion.colors) ? suggestion.colors : [],
            season: suggestion.season || "",
            occasions: suggestion.occasions || [],
            title: (suggestion.title || "").trim(),
            details: suggestion.details || "",
            image: dataUrl,
            userId: auth.currentUser.uid,
            createdAt: new Date(),
          };

          const id = await addClothing(payload);
          const addedItem = { id, ...payload };
          setLastAdded(addedItem);
        } catch (fileErr) {
          // Mark as failed and continue
          setFailed((n) => n + 1);
        } finally {
          setProcessed((n) => n + 1);
        }
        // Inter-item delay for quotas
        await sleep(2000);
      }
    } finally {
      runningRef.current = false;
      setIsActive(false);
    }
  }, [analyzeWithRetry, computeMaxClothes]);

  const hideBanner = useCallback(() => setHidden(true), []);

  const value = useMemo(() => ({
    isActive,
    total,
    processed,
    failed,
    hidden,
    lastAdded,
    startBulkImport,
    hideBanner,
  }), [isActive, total, processed, failed, hidden, lastAdded, startBulkImport, hideBanner]);

  return (
    <ImportContext.Provider value={value}>
      {children}
    </ImportContext.Provider>
  );
};

export default ImportContext;

